/**
 * Dashboard Page
 * Overview of contracts, payments, and key metrics
 * Supports filtering by investor country and configurable base currency
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  TextField,
  Collapse,
  IconButton,
  Button,
} from '@mui/material';
import {
  TrendingUp,
  Description,
  Payment as PaymentIcon,
  Warning,
  ErrorOutline,
  EventAvailable,
  LinkOff,
  Groups,
  ExpandMore,
  ExpandLess,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { apiGet } from '../utils/apiClient';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatCard from '../components/common/StatCard';
import ContractReferenceCell from '../components/contracts/ContractReferenceCell';
import { format } from 'date-fns';
import { formatNumber } from '../utils/validation';
import { exportUpcomingEvents } from '../utils/excelExport';
import { useTenant } from '../contexts/TenantContext';
import { usePermissions } from '../contexts/PermissionContext';
import { MODULES } from '../contexts/PermissionConstants';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const CURRENCY_SYMBOLS = {
  USD: '$', ZAR: 'R', GBP: '£', EUR: '€',
  BWP: 'P', NAD: 'N$', LSL: 'M',
};

const compactMoney = (value, symbol) => {
  const v = Number(value) || 0;
  if (Math.abs(v) >= 1_000_000) return `${symbol} ${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${symbol} ${(v / 1_000).toFixed(1)}K`;
  return `${symbol} ${formatNumber(v)}`;
};

function Dashboard() {
  const navigate = useNavigate();
  const { tenant, loading: tenantLoading, getStakeholderLabel } = useTenant();
  const { canRead } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [statusDisplayMode, setStatusDisplayMode] = useState('percent');
  const [productDisplayMode, setProductDisplayMode] = useState('percent');
  const [approvalsTotal, setApprovalsTotal] = useState(0);
  const [approvalsSummary, setApprovalsSummary] = useState(null);
  const [eventsExpanded, setEventsExpanded] = useState(false);
  const [capacityExpanded, setCapacityExpanded] = useState(false);

  // Filter state
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableCurrencies, setAvailableCurrencies] = useState(['USD', 'ZAR']);

  const refDataLoaded = useRef(false);

  const currencySymbol = CURRENCY_SYMBOLS[baseCurrency] || baseCurrency;
  const issuerLabel = getStakeholderLabel
    ? getStakeholderLabel('stakeholder.issuer.label', 'Issuer')
    : 'Issuer';

  const makeDisplayChangeHandler = (setter) => (event, newMode) => {
    if (newMode !== null) setter(newMode);
  };

  const loadFilterOptions = useCallback(async () => {
    if (refDataLoaded.current) return;
    try {
      const countriesData = await apiGet('/refdata/countries?active_only=true');
      const countries = Array.isArray(countriesData) ? countriesData
        : (countriesData?.data || countriesData?.rows || []);
      setAvailableCountries(countries);

      const currencies = [...new Set(countries.map(c => c.currency_code).filter(Boolean))].sort();
      if (currencies.length > 0) {
        setAvailableCurrencies(currencies);
      }
      refDataLoaded.current = true;
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const urlParams = new URLSearchParams(window.location.search);
    const tenantParam = urlParams.get('tenant');
    const storedTenantId = localStorage.getItem('selectedTenantId');

    if (!tenantLoading && (tenantParam || storedTenantId || tenant?.id)) {
      loadFilterOptions();
      if (!cancelled) loadDashboardData();
      if (!cancelled && canRead(MODULES.CONTRACT_MANAGEMENT)) {
        loadApprovalsSummary();
      }
    } else if (!tenantLoading && !tenantParam && !storedTenantId && !tenant?.id) {
      setLoading(false);
      setDashboardData(getEmptyDashboardData());
    }
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantLoading, tenant?.id]);

  const filtersInitialized = useRef(false);
  useEffect(() => {
    if (!filtersInitialized.current) {
      filtersInitialized.current = true;
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const tenantParam = urlParams.get('tenant');
    const storedTenantId = localStorage.getItem('selectedTenantId');
    if (!tenantLoading && (tenantParam || storedTenantId || tenant?.id)) {
      loadDashboardData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountries, baseCurrency]);

  const loadApprovalsSummary = async () => {
    try {
      const response = await apiGet('/approvals/summary');
      if (response.success) {
        const s = response.data || {};
        const sum = Number(s.contract_approvals || 0)
                  + Number(s.verifications || 0);
        setApprovalsTotal(sum);
        setApprovalsSummary(s);
      }
    } catch (err) {
      console.error('Error loading approvals summary:', err);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCountries.length > 0) {
        params.set('country_ids', selectedCountries.map(c => c.id).join(','));
      }
      params.set('base_currency', baseCurrency);

      const queryString = params.toString();
      const apiData = await apiGet(`/reports/dashboard?${queryString}`);

      const transformedData = transformApiData(apiData);
      setDashboardData(transformedData);
      setError(null);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      if (err.response?.status !== 429) {
        setError('Failed to load dashboard data');
      }
      setDashboardData(getEmptyDashboardData());
    } finally {
      setLoading(false);
    }
  };

  const getEmptyDashboardData = () => ({
    totalPortfolioValue: 0,
    capitalByCurrency: [],
    activeContracts: 0,
    pendingApprovals: 0,
    upcomingPayments: [],
    upcomingPaymentsCount: 0,
    upcomingPaymentsTotal: 0,
    contractsByStatus: [],
    contractsByProduct: [],
    contractsByInvestor: [],
    capitalByIssuer: [],
    programCapacity: [],
    unmatched: { count: 0, total_amount: 0, overdue_count: 0, upcoming_count: 0, overdue_total: 0, upcoming_total: 0 },
    overdue: { count: 0, total_amount: 0, by_currency: [] },
    maturing: { d30: 0, d60: 0, d90: 0, cap30: 0, cap60: 0, cap90: 0 },
  });

  const transformApiData = (apiData) => {
    let activeContracts = 0;
    const contractsByStatus = [];

    if (apiData.contractStats && Array.isArray(apiData.contractStats)) {
      apiData.contractStats.forEach(stat => {
        const count = parseInt(stat.count) || 0;

        contractsByStatus.push({
          name: stat.status || 'Unknown',
          value: count,
        });

        if (stat.status === 'Active') {
          activeContracts = count;
        }
      });
    }

    if (apiData.activeContractsCount) {
      activeContracts = parseInt(apiData.activeContractsCount) || activeContracts;
    }

    const upcomingPaymentsCount = apiData.upcomingPayments?.count
      ? parseInt(apiData.upcomingPayments.count)
      : 0;
    const upcomingPaymentsTotal = parseFloat(apiData.upcomingPayments?.total_amount) || 0;

    const upcomingPayments = (apiData.upcomingPayments?.data || []).map(p => {
      const rawAmount = p.amount;
      const hasAmount = rawAmount !== null && rawAmount !== undefined && rawAmount !== '';
      return {
        id: p.id,
        contractId: p.contract_id,
        reference: p.contract_reference || p.reference,
        userReference: p.user_reference,
        investor: p.investor_name || p.investor,
        paymentDate: p.payment_date || p.paymentDate,
        amount: hasAmount ? parseFloat(rawAmount) : null,
        currency: p.currency_code || p.currency || 'ZAR',
        type: p.payment_type || p.type || 'Interest',
      };
    });

    const contractsByProduct = (apiData.contractsByProduct || []).map(p => ({
      name: p.product_code || p.product_name || p.name,
      value: parseInt(p.count) || parseInt(p.value) || 0,
      totalCapital: parseFloat(p.total_capital) || 0,
    }));

    const investorRows = (apiData.capitalByInvestor || []).map(i => ({
      name: i.investor_name || i.name,
      value: parseFloat(i.total_capital) || parseFloat(i.value) || 0,
    }));
    const contractsByInvestor = investorRows.length > 10
      ? [
          ...investorRows.slice(0, 9),
          {
            name: 'Other',
            value: investorRows.slice(9).reduce((sum, r) => sum + r.value, 0),
          },
        ]
      : investorRows;

    const capitalByIssuer = (apiData.capitalByIssuer || []).map(i => ({
      name: i.issuer_name,
      value: parseFloat(i.total_capital) || 0,
      contractCount: parseInt(i.contract_count) || 0,
    }));

    // programCapacity (T096): each entry already arrives sorted by utilisation
    // DESC NULLS LAST (closest-to-breach first). limit_native NULL = "no limit".
    const programCapacity = (apiData.programCapacity || []).map(p => {
      const limitBase = p.limit_base != null ? parseFloat(p.limit_base) : null;
      const usedBase = parseFloat(p.used_base) || 0;
      const utilisation = p.utilisation != null ? parseFloat(p.utilisation) : null;
      // Stacked-bar segments (in base ccy): used + headroom = limit.
      // The DB hard-blocks over-limit contracts via fn_program_check_limit,
      // so usedBase will never exceed limitBase. NULL-limit programs show
      // used only (no headroom segment) — labelled "No limit" in the tooltip.
      const usedSegment = usedBase;
      const headroomSegment = limitBase != null ? Math.max(limitBase - usedBase, 0) : 0;
      return {
        programId: p.program_id,
        programName: p.program_name,
        issuerId: p.issuer_id,
        issuerName: p.issuer_name,
        currency: p.currency_code,
        limitNative: p.limit_native != null ? parseFloat(p.limit_native) : null,
        usedNative: parseFloat(p.used_native) || 0,
        limitBase,
        usedBase,
        utilisation,
        usedSegment,
        headroomSegment,
      };
    });

    const capitalByCurrency = (apiData.capitalByCurrency || []).map(c => ({
      currency: c.currency_code,
      symbol: c.currency_symbol,
      amount: parseFloat(c.total_amount) || 0,
      baseEquivalent: parseFloat(c.base_equivalent ?? c.zar_equivalent) || 0,
      contractCount: parseInt(c.contract_count) || 0,
      fxRate: parseFloat(c.fx_rate) || null,
    }));

    const totalPortfolioValue = parseFloat(apiData.totalPortfolioValue ?? apiData.totalPortfolioZAR) || 0;

    const unmatched = apiData.unmatchedPayments || {};
    const overdue = apiData.overduePayments || {};
    const maturing = apiData.maturingSoon || {};

    return {
      totalPortfolioValue,
      capitalByCurrency,
      activeContracts,
      upcomingPayments,
      upcomingPaymentsCount,
      upcomingPaymentsTotal,
      contractsByStatus,
      contractsByProduct,
      contractsByInvestor,
      capitalByIssuer,
      programCapacity,
      unmatched: {
        count: parseInt(unmatched.count) || 0,
        total_amount: parseFloat(unmatched.total_amount) || 0,
        overdue_count: parseInt(unmatched.overdue_count) || 0,
        upcoming_count: parseInt(unmatched.upcoming_count) || 0,
        overdue_total: parseFloat(unmatched.overdue_total) || 0,
        upcoming_total: parseFloat(unmatched.upcoming_total) || 0,
      },
      overdue: {
        count: parseInt(overdue.count) || 0,
        total_amount: parseFloat(overdue.total_amount) || 0,
        by_currency: Array.isArray(overdue.by_currency) ? overdue.by_currency : [],
      },
      maturing: {
        d30: parseInt(maturing.d30) || 0,
        d60: parseInt(maturing.d60) || 0,
        d90: parseInt(maturing.d90) || 0,
        cap30: parseFloat(maturing.cap30) || 0,
        cap60: parseFloat(maturing.cap60) || 0,
        cap90: parseFloat(maturing.cap90) || 0,
      },
    };
  };

  const handleEventClick = (event) => {
    if (event.type === 'Re-pricing') {
      navigate(`/contracts/${event.contractId}`);
      return;
    }
    // Payment event → Matching, auto-open the match modal for this schedule
    // row. event.id is the contract_amortization_schedule id, which the
    // Matching feeds key on, so the page can find the row and open the dialog.
    navigate(`/payments?scheduleId=${event.id}`);
  };

  if (loading || tenantLoading) {
    return <LoadingSpinner fullPage />;
  }

  const data = dashboardData || getEmptyDashboardData();

  // Top product breakdown (top 3) for the Active Contracts stat card.
  // 3-col layout: count · product name · base-currency capital.
  const topProductBreakdown = data.contractsByProduct.slice(0, 3).map(p => ({
    count: p.value,
    label: p.name,
    value: compactMoney(p.totalCapital, currencySymbol),
  }));

  // Maturing Soon: 3-col layout per disjoint window (count · window · base-ccy
  // capital). Buckets are non-overlapping (T093) so each contract lands in one.
  const maturingBreakdown = [
    { count: data.maturing.d30, label: '0–30 days',  value: compactMoney(data.maturing.cap30, currencySymbol) },
    { count: data.maturing.d60, label: '31–60 days', value: compactMoney(data.maturing.cap60, currencySymbol) },
    { count: data.maturing.d90, label: '61–90 days', value: compactMoney(data.maturing.cap90, currencySymbol) },
  ];
  const maturingTotalCount = (data.maturing.d30 || 0) + (data.maturing.d60 || 0) + (data.maturing.d90 || 0);

  // Top Issuer / Investor Group concentration. Data from capitalByIssuer
  // (top 10 by base-ccy capital, sorted DESC). Headline = the single largest
  // counterparty; breakdown = top 3 (count · name · base-ccy capital).
  // Placeholder until per-program capacity tracking lands.
  const topIssuer = data.capitalByIssuer[0] || null;
  const topIssuerPct = topIssuer && data.totalPortfolioValue > 0
    ? Math.round((topIssuer.value / data.totalPortfolioValue) * 100)
    : null;
  const issuerBreakdown = data.capitalByIssuer.slice(0, 3).map(i => ({
    count: i.contractCount,
    label: i.name,
    value: compactMoney(i.value, currencySymbol),
  }));

  // Unmatched Payments: 3-col layout per bucket (count · status · base-ccy total).
  const unmatchedBreakdown = [
    { count: data.unmatched.overdue_count,  label: 'Overdue',  value: compactMoney(data.unmatched.overdue_total,  currencySymbol) },
    { count: data.unmatched.upcoming_count, label: 'Upcoming', value: compactMoney(data.unmatched.upcoming_total, currencySymbol) },
  ];

  // Pending Approvals: per-type counts from /approvals/summary. Labels match
  // ApprovalsPage.js exactly. 2-col layout — these rows don't carry $ values.
  // Hide zero-count rows so empty states stay tidy.
  const approvalsBreakdown = approvalsSummary
    ? [
        { key: 'new_contracts',  label: 'New Contracts'   },
        { key: 'rate_changes',   label: 'Rate Changes'    },
        { key: 'finalisations',  label: 'Finalisations'   },
        { key: 'amendments',     label: 'Other Amendments' },
        { key: 'verifications',  label: 'Verifications'   },
      ]
        .map(({ key, label }) => ({ label, value: Number(approvalsSummary[key] || 0) }))
        .filter(r => r.value > 0)
    : [];

  // Top currency breakdown for the Total Portfolio stat card
  const topCurrencyBreakdown = data.capitalByCurrency.slice(0, 3).map(c => ({
    label: c.currency,
    value: compactMoney(c.baseEquivalent, currencySymbol),
  }));

  const overdueCurrencyBreakdown = (data.overdue.by_currency || []).slice(0, 3).map(c => ({
    label: c.currency_code,
    value: `${c.currency_code} ${formatNumber(parseFloat(c.total_amount) || 0)}`,
  }));

  // Type breakdown for the Payments (30d) card. NOTE: derived from the
  // upcomingPayments.data list which is capped at 20 rows server-side, so
  // for tenants with >20 upcoming events the buckets won't reconcile to
  // the headline count. TODO: server-side aggregation when we paginate
  // events. See project_dashboard_followups memory.
  // Base-currency conversion uses fxRate from capitalByCurrency (same
  // pivot-cross-rate that the rest of the dashboard already shows).
  const fxByCurrency = (data.capitalByCurrency || []).reduce((acc, c) => {
    if (c.currency && c.fxRate != null) acc[c.currency] = c.fxRate;
    return acc;
  }, {});
  const paymentsByType = (data.upcomingPayments || []).reduce((acc, p) => {
    const t = p.type === 'Interest + Capital' ? 'combined'
      : (p.type === 'Capital' ? 'capital' : 'interest');
    acc[t].count += 1;
    if (p.amount != null) {
      const fx = fxByCurrency[p.currency] ?? 1;
      acc[t].value += p.amount * fx;
    }
    return acc;
  }, {
    interest: { count: 0, value: 0 },
    capital:  { count: 0, value: 0 },
    combined: { count: 0, value: 0 },
  });
  const paymentsTypeBreakdown = [
    { count: paymentsByType.interest.count, label: 'Interest', value: compactMoney(paymentsByType.interest.value, currencySymbol) },
    { count: paymentsByType.capital.count,  label: 'Capital',  value: compactMoney(paymentsByType.capital.value,  currencySymbol) },
    { count: paymentsByType.combined.count, label: 'Combined', value: compactMoney(paymentsByType.combined.value, currencySymbol) },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      {error && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
          <Typography color="warning.main">{error} </Typography>
        </Box>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Autocomplete
          multiple
          size="small"
          options={availableCountries}
          getOptionLabel={(option) => `${option.country} (${option.country_code})`}
          value={selectedCountries}
          onChange={(_, newValue) => setSelectedCountries(newValue)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} label="Filter by Country" placeholder="All Countries" />
          )}
          sx={{ minWidth: 300, flex: 1 }}
        />
        <Autocomplete
          size="small"
          options={availableCurrencies}
          getOptionLabel={(option) => option}
          value={baseCurrency}
          onChange={(_, newValue) => setBaseCurrency(newValue || 'USD')}
          disableClearable
          renderInput={(params) => (
            <TextField {...params} label="Base Currency" />
          )}
          sx={{ minWidth: 150 }}
        />
      </Paper>

      {/* Row 1 — portfolio shape: Portfolio, Active, Pending, Maturing Soon */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Portfolio Value"
            value={compactMoney(data.totalPortfolioValue, currencySymbol)}
            subtitle={`All currencies in ${baseCurrency}`}
            Icon={TrendingUp}
            color="#00C49F"
            breakdown={topCurrencyBreakdown.length > 0 ? topCurrencyBreakdown : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Active Contracts"
            value={data.activeContracts}
            subtitle="Currently in force"
            Icon={Description}
            color="#0088FE"
            onClick={() => navigate('/contracts')}
            breakdown={topProductBreakdown.length > 0 ? topProductBreakdown : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Pending Approvals"
            value={approvalsTotal}
            subtitle="Require action"
            Icon={Warning}
            color="#FF8042"
            onClick={() => navigate('/approvals')}
            breakdown={approvalsBreakdown.length > 0 ? approvalsBreakdown : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Maturing Soon"
            value={maturingTotalCount}
            subtitle="Contracts within 90 days"
            Icon={EventAvailable}
            color="#FF8042"
            onClick={() => navigate('/contracts?status=Active&sort=maturity_date&dir=asc')}
            breakdown={maturingBreakdown}
          />
        </Grid>
      </Grid>

      {/* Row 2 — action items: Overdue, Unmatched, Payments (30d), Capital Maturing */}
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Overdue Payments"
            value={data.overdue.count}
            subtitle={data.overdue.count > 0 ? 'Past due, unmatched' : 'None overdue'}
            Icon={ErrorOutline}
            color="#D32F2F"
            onClick={() => navigate('/payments?tab=matching')}
            breakdown={overdueCurrencyBreakdown.length > 0 ? overdueCurrencyBreakdown : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Unmatched Payments"
            value={data.unmatched.count}
            subtitle="Awaiting allocation"
            Icon={LinkOff}
            color="#9C27B0"
            onClick={() => navigate('/payments?tab=matching')}
            breakdown={unmatchedBreakdown}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box sx={{ position: 'relative' }}>
            <StatCard
              title="Payments (30 days)"
              value={data.upcomingPaymentsCount}
              subtitle={eventsExpanded ? 'Hide events' : 'Click to view events'}
              Icon={PaymentIcon}
              color="#FFBB28"
              selected={eventsExpanded}
              onClick={() => setEventsExpanded(prev => !prev)}
              breakdown={paymentsTypeBreakdown}
            />
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setEventsExpanded(prev => !prev); }}
              sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper' }}
              aria-label={eventsExpanded ? 'Collapse upcoming events' : 'Expand upcoming events'}
            >
              {eventsExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title={`Top ${issuerLabel}`}
            value={
              <Typography
                variant="h6"
                component="div"
                sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={topIssuer ? topIssuer.name : ''}
              >
                {topIssuer ? topIssuer.name : '—'}
              </Typography>
            }
            subtitle={topIssuerPct != null ? `${topIssuerPct}% of portfolio` : `Largest ${issuerLabel.toLowerCase()} exposure`}
            Icon={Groups}
            color="#9C27B0"
            breakdown={issuerBreakdown.length > 0 ? issuerBreakdown : undefined}
          />
        </Grid>
      </Grid>

      {/* Upcoming Events — collapsed by default, toggled by the Payments card.
          Click a row to deeplink into Matching pre-filtered to that contract. */}
      <Collapse in={eventsExpanded} timeout="auto" unmountOnExit>
        <Card sx={{ mb: 3, mt: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h6">Upcoming Events (Next 30 Days)</Typography>
                <Typography variant="body2" color="text.secondary">
                  {(data.upcomingPayments || []).length} event{(data.upcomingPayments || []).length === 1 ? '' : 's'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Click a row to open it in Matching
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => exportUpcomingEvents(data.upcomingPayments || [])}
                  disabled={!data.upcomingPayments || data.upcomingPayments.length === 0}
                >
                  Export
                </Button>
              </Box>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Reference</TableCell>
                    <TableCell>Investor</TableCell>
                    <TableCell>Payment Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.upcomingPayments || []).map((payment, idx) => {
                    const isRepricing = payment.type === 'Re-pricing';
                    let chipColor = 'secondary';
                    if (isRepricing) chipColor = 'warning';
                    else if (payment.type?.includes('Capital')) chipColor = 'primary';
                    return (
                      <TableRow
                        key={`${payment.type}-${payment.id}-${payment.paymentDate}-${idx}`}
                        hover
                        onClick={() => handleEventClick(payment)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <ContractReferenceCell reference={payment.reference} userReference={payment.userReference} />
                        </TableCell>
                        <TableCell>{payment.investor}</TableCell>
                        <TableCell>{format(new Date(payment.paymentDate), 'dd MMM yyyy')}</TableCell>
                        <TableCell>
                          <Chip label={payment.type} size="small" color={chipColor} />
                        </TableCell>
                        <TableCell align="right">
                          {payment.amount != null
                            ? `${payment.currency} ${formatNumber(payment.amount)}`
                            : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(!data.upcomingPayments || data.upcomingPayments.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="textSecondary">No upcoming events</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Collapse>

      <Box sx={{ mb: 2 }} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Row 1: Program Capacity (stacked bars, half) | Status pie | Product pie (quarter each).
            Used + remaining headroom = limit; over-limit shows a red overflow segment.
            Sorted closest-to-breach (backend sort). Top 5 shown by default; expand for the rest. */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                  <Groups sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                  Program Capacity ({data.programCapacity.length})
                </Typography>
                {data.programCapacity.length > 5 && (
                  <Button
                    size="small"
                    onClick={() => setCapacityExpanded(prev => !prev)}
                    endIcon={capacityExpanded ? <ExpandLess /> : <ExpandMore />}
                  >
                    {capacityExpanded ? 'Show top 5' : `Show all ${data.programCapacity.length}`}
                  </Button>
                )}
              </Box>
              {data.programCapacity.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(300, (capacityExpanded ? data.programCapacity.length : Math.min(5, data.programCapacity.length)) * 56)}>
                  <BarChart
                    data={capacityExpanded ? data.programCapacity : data.programCapacity.slice(0, 5)}
                    layout="vertical"
                    margin={{ left: 80, right: 16 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(value) => `${currencySymbol} ${(value / 1_000_000).toFixed(1)}M`} />
                    <YAxis type="category" dataKey="programName" width={140} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value, name) => [compactMoney(value, currencySymbol), name]}
                      labelFormatter={(label, payload) => {
                        const p = payload && payload[0]?.payload;
                        if (!p) return label;
                        const util = p.utilisation != null ? `${(p.utilisation * 100).toFixed(1)}%` : 'No limit';
                        return `${p.programName} · ${p.issuerName} · ${util}`;
                      }}
                    />
                    <Legend />
                    <Bar dataKey="usedSegment"     stackId="cap" fill="#2E7D32" name="Used" />
                    <Bar dataKey="headroomSegment" stackId="cap" fill="#D32F2F" name="Headroom" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No active programs</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Contracts by Status (pie) */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Contracts by Status
                </Typography>
                <ToggleButtonGroup
                  value={statusDisplayMode}
                  exclusive
                  onChange={makeDisplayChangeHandler(setStatusDisplayMode)}
                  size="small"
                >
                  <ToggleButton value="percent" sx={{ px: 1, py: 0.25 }}>%</ToggleButton>
                  <ToggleButton value="number" sx={{ px: 1, py: 0.25 }}>#</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {data.contractsByStatus.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      key={statusDisplayMode}
                      data={data.contractsByStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent, value }) =>
                        statusDisplayMode === 'percent'
                          ? `${name} ${(percent * 100).toFixed(0)}%`
                          : `${name} (${value})`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {data.contractsByStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No contract data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Contracts by Product (pie) — paired beside Status */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Contracts by Product Type
                </Typography>
                <ToggleButtonGroup
                  value={productDisplayMode}
                  exclusive
                  onChange={makeDisplayChangeHandler(setProductDisplayMode)}
                  size="small"
                >
                  <ToggleButton value="percent" sx={{ px: 1, py: 0.25 }}>%</ToggleButton>
                  <ToggleButton value="number" sx={{ px: 1, py: 0.25 }}>#</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {data.contractsByProduct.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      key={productDisplayMode}
                      data={data.contractsByProduct}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent, value }) =>
                        productDisplayMode === 'percent'
                          ? `${name} ${(percent * 100).toFixed(0)}%`
                          : `${name} (${value})`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {data.contractsByProduct.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No product data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Row 2: Top Investors (bar) | Capital by Currency (table) */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Top Investors by Capital Deployed
              </Typography>
              {data.contractsByInvestor.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.contractsByInvestor}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(value) => `${currencySymbol} ${(value / 1000000).toFixed(0)}M`} />
                    <Tooltip formatter={(value) => [`${currencySymbol} ${(value / 1000000).toFixed(2)}M`, 'Capital']} />
                    <Legend />
                    <Bar dataKey="value" fill="#0088FE" name={`Capital (${baseCurrency})`}>
                      {data.contractsByInvestor.map((entry) => (
                        <Cell key={entry.name} fill={entry.name === 'Other' ? '#A0A0A0' : '#0088FE'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No investor data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Capital by Currency — moved up, paired beside Top Investors */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Capital by Currency
              </Typography>
              {data.capitalByCurrency.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Currency</TableCell>
                        <TableCell align="right">Contracts</TableCell>
                        <TableCell align="right">LCY Amount</TableCell>
                        <TableCell align="right">Rate</TableCell>
                        <TableCell align="right">{baseCurrency} Equivalent</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.capitalByCurrency.map((curr) => (
                        <TableRow key={curr.currency}>
                          <TableCell><strong>{curr.currency}</strong></TableCell>
                          <TableCell align="right">{curr.contractCount}</TableCell>
                          <TableCell align="right">{formatNumber(curr.amount)}</TableCell>
                          <TableCell align="right">
                            {curr.fxRate != null ? curr.fxRate.toFixed(4) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {currencySymbol} {formatNumber(curr.baseEquivalent)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell colSpan={4}><strong>Total Portfolio</strong></TableCell>
                        <TableCell align="right">
                          <strong>{currencySymbol} {formatNumber(data.totalPortfolioValue)}</strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No currency data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
